const INTERNAL_INVOICE_APPROVERS = new Set(['mays', 'david', 'brad']);

export function invoiceApprovalPersonKey(profile = {}) {
  const name = String(profile.name || profile.fullName || '').trim().toLowerCase();
  const email = String(profile.email || '').trim().toLowerCase();
  const first = name.split(/\s+/)[0] || '';
  if (INTERNAL_INVOICE_APPROVERS.has(first)) return first;
  for (const key of INTERNAL_INVOICE_APPROVERS) {
    if (email.startsWith(`${key}.`) || email.startsWith(`${key}@`)) return key;
  }
  return '';
}

export function isInternalInvoiceApprovalProfile(profile) {
  return !!invoiceApprovalPersonKey(profile);
}

export function commissionPayoutApprovalKey({ roleKey, userId } = {}) {
  const role = String(roleKey || '').trim().toLowerCase();
  const id = String(userId || '').trim().toLowerCase();
  return role && id ? `${role}:${id}` : '';
}

/**
 * On a deal split across several buyers, the buy-side agent approves one
 * line covering the outside buyers' buy-side commission plus the
 * Paraclete purchase commission on whatever Paraclete bought for itself.
 *
 * `multiBuyer` is the result of buySideApprovalForMultiBuyerDeal, which
 * also decides what happens to the sell side. Where it folded the sell
 * side into that total (the same person holds both sides), the sell-side
 * row is dropped, because leaving both would gate the payout email on
 * the same money twice and overstate what the deal pays. Where a
 * different agent carries the sell side, `sellSideIsSeparate` is set,
 * the total excludes it, and that agent keeps their own row — the ledger
 * pays them separately either way, so dropping it left them uncredited
 * and made the buy-side agent approve money they were never paid.
 */
export function commissionPayoutApprovalTargetsForDeal(staffUsers = [], deal = {}, splits = {}, multiBuyer = null) {
  const staffById = new Map((staffUsers || []).filter((user) => user?.id).map((user) => [String(user.id), user]));
  const rows = [
    {
      roleKey: 'buySide',
      roleLabel: multiBuyer
        ? 'Buy-side agent (all buyers)'
        : (splits?.isParacleteBuyer ? 'Buy-side agent (Paraclete purchase)' : 'Buy-side agent'),
      userId: deal?.buySideAgentId,
      amount: multiBuyer
        ? Number(multiBuyer.total) || 0
        : Number(splits?.isParacleteBuyer ? (splits?.paracleteBuySide ?? splits?.buySide) : splits?.buySide) || 0,
      pctLabel: multiBuyer
        ? (multiBuyer.sellSideIsSeparate
          ? 'Buy side on outside buyers, plus 50% Paraclete purchase'
          : 'Buy + sell side on outside buyers, plus 50% Paraclete purchase')
        : (splits?.isParacleteBuyer
          ? (splits?.coordinator ? 'Remainder of 50%' : '50%')
          : (splits?.coordinator ? 'Remainder of 40%' : '40%')),
      // The approver needs to see what the total is made of — a lump
      // sum on an approval screen cannot be checked.
      parts: multiBuyer ? multiBuyer.parts : null,
    },
    {
      roleKey: 'coordinator',
      roleLabel: 'Coordinator',
      // A Paraclete self-purchase pays the coordinator out of the 50%
      // pool, so they approve their line here like on any other deal.
      // Nulling the id used to drop the row entirely (the userId filter
      // below), which is why coordinators showed nothing on those deals.
      userId: deal?.coordinatorId,
      amount: Number(splits?.coordinator) || 0,
      pctLabel: splits?.coordinatorRate
        ? `${Math.round(Number(splits.coordinatorRate) * 100)}% ${splits?.coordinatorMarginBased === false ? 'of buy-side pool' : 'of margin'}`
        : '',
    },
    {
      // Optional: no manager named on the deal, no row — the userId
      // filter below drops it. When there is one they approve their
      // own line like everybody else splitting the deal, because
      // funding writes them a payout row too.
      roleKey: 'dispositionManager',
      roleLabel: 'Disposition manager',
      userId: deal?.dispositionManagerId,
      amount: Number(splits?.dispositionManager) || 0,
      pctLabel: splits?.dispositionManagerRate
        ? `${Math.round(Number(splits.dispositionManagerRate) * 100)}% of margin, from the Operating Fund`
        : '',
    },
    {
      roleKey: 'sellSide',
      // On an investor deal the investor's rep TAKES this slot — same
      // 10%, different recipient — so this row is theirs. It used to
      // name deal.sellSideAgentId with splits.sellSide, which on an
      // investor deal is the agent from back when the deal had a buyer,
      // for an amount of zero. The rep was owed the share, was written a
      // payout row at funding, and never appeared on the approval screen
      // to approve it.
      roleLabel: splits?.sellSideReplacedByInvestorRep
        ? 'Investor rep (sell-side share)'
        : multiBuyer?.sellSideIsSeparate ? 'Sell-side agent (outside buyers)' : 'Sell-side agent',
      userId: splits?.sellSideReplacedByInvestorRep
        ? (splits?.investorRepId || splits?.sellSideAgentId || null)
        : deal?.sellSideAgentId,
      // On a mixed deal, `splits.sellSide` is 10% of the WHOLE margin,
      // Paraclete-held tracts included — and nobody pays a sell-side
      // commission on a tract Paraclete bought for itself. The
      // multi-buyer summary already separates the two pools, so the
      // row carries the outside-buyer figure when there is one.
      amount: splits?.sellSideReplacedByInvestorRep
        ? Number(splits?.investorRep) || 0
        : multiBuyer?.sellSideIsSeparate
          ? Number(multiBuyer.outsideSellSide) || 0
          : Number(splits?.sellSide) || 0,
      // Per-profile sell-side rates (set at invite) surface here too, and
      // an investor's own rep rate the same way.
      pctLabel: splits?.sellSideReplacedByInvestorRep
        ? `${Math.round((Number(splits?.investorRepRate) || 0.10) * 100)}% of margin, as the investor's rep`
        : `${Math.round((Number(splits?.sellSideRate) || 0.10) * 100)}%`
          + (multiBuyer?.sellSideIsSeparate ? ' of the outside-buyer margin' : ''),
    },
  ];
  return rows
    .filter((row) => row.userId && (row.roleKey !== 'coordinator' || row.amount !== 0))
    // Paraclete self-purchases pay no sell-side commission — don't force
    // the sell-side agent to approve a $0 payout (it blocks the payout
    // email behind an approval that means nothing).
    .filter((row) => !(row.roleKey === 'sellSide' && splits?.isParacleteBuyer))
    // Dropped only where its amount really is inside the buy-side line.
    // With a separate sell-side agent it is not, and dropping it would
    // leave the person the ledger pays with nothing to approve.
    .filter((row) => !(row.roleKey === 'sellSide' && multiBuyer && !multiBuyer.sellSideIsSeparate))
    .map((row) => {
      const user = staffById.get(String(row.userId)) || { id: row.userId };
      const key = commissionPayoutApprovalKey({ roleKey: row.roleKey, userId: row.userId });
      return {
        ...row,
        key,
        user,
        userName: user.name || user.email || row.userId,
      };
    });
}

export function isInvoiceApprovalEntry(entry = {}) {
  return entry?.type === 'invoice-approval' || entry?.approvalType === 'agent-invoice';
}

export function invoiceApprovalKeyFromEntry(entry = {}) {
  return String(entry.approverKey || entry.approverFirstName || '').trim().toLowerCase();
}

export function approvedInvoiceKeys(entries = []) {
  const keys = new Set();
  (entries || []).forEach((entry) => {
    if (!isInvoiceApprovalEntry(entry)) return;
    const key = invoiceApprovalKeyFromEntry(entry);
    if (key) keys.add(key);
  });
  return keys;
}

export function buildInvoiceApprovalEntry({ dealId, user, targetUser, approvalTarget = {} } = {}) {
  const target = approvalTarget?.key ? approvalTarget : (targetUser || user || {});
  const key = target.key || invoiceApprovalPersonKey(target) || invoiceApprovalPersonKey(user);
  const now = new Date().toISOString();
  const approverName = user?.name || user?.email || target?.name || target?.email || 'Approver';
  const targetName = target.userName || target?.name || target?.email || approverName;
  return {
    id: `invoice-approval-${dealId || 'deal'}-${key || 'internal'}-${Date.now()}`,
    type: 'invoice-approval',
    approvalType: 'agent-invoice',
    label: `${targetName} Commission Payout Approval`,
    fileName: `${targetName} approved commission payout`,
    approved: true,
    approverKey: key,
    approverProfileId: user?.id || null,
    approverName,
    approvedForProfileId: target.userId || target?.id || null,
    approvedForName: targetName,
    approvedForRole: target.roleKey || null,
    approvedForRoleLabel: target.roleLabel || null,
    approvedAmount: Number.isFinite(Number(target.amount)) ? Number(target.amount) : null,
    approvedPctLabel: target.pctLabel || null,
    approvedAt: now,
    uploaded: false,
    executed: true,
    source: 'internal-invoice-approval',
  };
}
